import { Suspense, lazy, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useMerchantDevicesHealthQuery } from '../hooks/useMerchantDevicesHealthQuery';
import type { MerchantDeviceHealthItem } from '../service/types';

interface DashboardDisplayStatusCardProps {
  branchId?: string;
  variant?: 'card' | 'compact';
}

const LazyDashboardDisplayStatusDialog = lazy(() =>
  import('./DashboardDisplayStatusDialog').then((module) => ({
    default: module.DashboardDisplayStatusDialog,
  })),
);

function formatRelativeTimestamp(raw: string | null): string | null {
  if (!raw) {
    return null;
  }

  const timestamp = new Date(raw);
  if (Number.isNaN(timestamp.getTime())) {
    return null;
  }

  return formatDistanceToNow(timestamp, { addSuffix: true });
}

function getCardDetailText(args: {
  t: TFunction;
  isLoading: boolean;
  isError: boolean;
  devices: MerchantDeviceHealthItem[];
  downDevices: MerchantDeviceHealthItem[];
}) {
  const { t, isLoading, isError, devices, downDevices } = args;
  if (isLoading && devices.length === 0) {
    return t('dashboard.display.checking');
  }

  if (isError && devices.length === 0) {
    return t('dashboard.display.load_error');
  }

  if (downDevices.length > 0) {
    const firstDownDevice = downDevices[0];
    const lastKnownGood = formatRelativeTimestamp(firstDownDevice?.last_display_ok_at ?? null);
    const detailParts = [];

    if (firstDownDevice?.last_display_error) {
      detailParts.push(firstDownDevice.last_display_error);
    }

    if (lastKnownGood) {
      detailParts.push(t('dashboard.display.last_working', { time: lastKnownGood }));
    }

    return detailParts.length > 0
      ? detailParts.join(' | ')
      : t('dashboard.display.devices_unreachable', { count: downDevices.length });
  }

  if (devices.length === 0) {
    return t('dashboard.display.no_data');
  }

  const allHealthy = devices.every((device) => device.display_ok === true);
  if (allHealthy) {
    return t('dashboard.display.devices_healthy', { count: devices.length });
  }

  return t('dashboard.display.unknown_some');
}

function DashboardDisplayStatusDialogFallback({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (nextOpen: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-y-auto sm:max-h-[90vh] sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('dashboard.display.dialog_title')}</DialogTitle>
          <DialogDescription>
            {t('dashboard.display.dialog_description')}
          </DialogDescription>
        </DialogHeader>
        <p className="text-muted-foreground text-sm">{t('dashboard.display.loading')}</p>
        <DialogFooter showCloseButton />
      </DialogContent>
    </Dialog>
  );
}

export function DashboardDisplayStatusCard({
  branchId,
  variant = 'card',
}: DashboardDisplayStatusCardProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const devicesHealthQuery = useMerchantDevicesHealthQuery({ branch_id: branchId });
  const devices = devicesHealthQuery.data?.devices ?? [];
  const downDevices = devices.filter((device) => device.display_ok === false);
  const allHealthy = devices.length > 0 && devices.every((device) => device.display_ok === true);
  const hasDownDevice = downDevices.length > 0;
  const headline = hasDownDevice
    ? t('dashboard.display.headline_down')
    : allHealthy
      ? t('dashboard.display.headline_ok')
      : t('dashboard.display.headline_unknown');
  const cardDetailText = getCardDetailText({
    t,
    isLoading: devicesHealthQuery.isPending,
    isError: devicesHealthQuery.isError,
    devices,
    downDevices,
  });

  return (
    <>
      <button
        type="button"
        className={cn('text-left', variant === 'card' ? 'w-full' : undefined)}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label={t('dashboard.display.open_details_aria')}
      >
        {variant === 'card' ? (
          <Card
            className={cn(
              'h-full gap-3 py-4 shadow-none transition-colors',
              hasDownDevice && 'border-destructive/40',
              allHealthy && 'border-emerald-500/35',
            )}
          >
            <CardHeader className="px-4 pb-1">
              <CardTitle className="text-muted-foreground text-sm font-medium">{t('dashboard.display.card_title')}</CardTitle>
            </CardHeader>
            <CardContent className="px-4">
              <div
                className={cn(
                  'flex items-center gap-2 text-lg leading-tight font-semibold',
                  hasDownDevice && 'text-destructive',
                  allHealthy && 'text-emerald-700 dark:text-emerald-300',
                )}
              >
                {hasDownDevice ? (
                  <XCircle className="h-5 w-5 shrink-0" aria-hidden="true" />
                ) : allHealthy ? (
                  <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden="true" />
                ) : (
                  <AlertTriangle
                    className="text-muted-foreground h-5 w-5 shrink-0"
                    aria-hidden="true"
                  />
                )}
                <span>{headline}</span>
              </div>
              <p className="text-muted-foreground mt-2 text-sm">{cardDetailText}</p>
              <p className="text-muted-foreground mt-3 text-xs">{t('dashboard.display.updates_every')}</p>
            </CardContent>
          </Card>
        ) : (
          <div
            className={cn(
              'inline-flex min-h-9 items-center gap-2 rounded-md border px-3 py-2 transition-colors',
              hasDownDevice && 'border-destructive/40 bg-destructive/5',
              allHealthy && 'border-emerald-500/35 bg-emerald-500/10',
            )}
          >
            <span className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
              {t('dashboard.display.card_title')}
            </span>
            <span
              className={cn(
                'inline-flex items-center gap-1.5 text-sm font-semibold',
                hasDownDevice && 'text-destructive',
                allHealthy && 'text-emerald-700 dark:text-emerald-300',
              )}
            >
              {hasDownDevice ? (
                <XCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
              ) : allHealthy ? (
                <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
              ) : (
                <AlertTriangle className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden="true" />
              )}
              {hasDownDevice
                ? t('dashboard.display.compact_down')
                : allHealthy
                  ? t('dashboard.display.compact_ok')
                  : t('dashboard.display.compact_unknown')}
            </span>
          </div>
        )}
      </button>

      {open ? (
        <Suspense fallback={<DashboardDisplayStatusDialogFallback open={open} onOpenChange={setOpen} />}>
          <LazyDashboardDisplayStatusDialog
            open={open}
            onOpenChange={setOpen}
            devices={devices}
            isPending={devicesHealthQuery.isPending}
            isError={devicesHealthQuery.isError}
            error={devicesHealthQuery.error}
            isRefetching={devicesHealthQuery.isRefetching}
            onRetry={() => {
              void devicesHealthQuery.refetch();
            }}
          />
        </Suspense>
      ) : null}
    </>
  );
}
